/* eslint-disable @eslint-react/dom/no-dangerously-set-innerhtml */
import { useUser } from '@folklore/auth';
import { useForm } from '@folklore/forms';
import { useUrlGenerator } from '@folklore/routes';
import classNames from 'classnames';
import { camelCase } from 'lodash';
import PropTypes from 'prop-types';
import { useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import useSetUser from '../../hooks/useSetUser';

import { useThemeClassName } from '../../contexts/ThemeContext';
import SubmitButton from '../buttons/SubmitButton';
import FieldRow from '../fields/FieldRow';

import styles from '../../../styles/forms/profile.module.scss';

const propTypes = {
    fields: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string,
            type: PropTypes.string,
        }),
    ),
    note: PropTypes.string,
    footerText: PropTypes.string,
    submitLabel: PropTypes.string,
    isCreate: PropTypes.bool,
    className: PropTypes.string,
    onSuccess: PropTypes.func,
};

const defaultProps = {
    fields: null,
    note: null,
    footerText: null,
    submitLabel: null,
    isCreate: false,
    className: null,
    onSuccess: null,
};

function ProfileForm({ fields, note, footerText, submitLabel, isCreate, className, onSuccess }) {
    const themeClassName = useThemeClassName(styles);

    const route = useUrlGenerator();
    const action = route('profile');
    const user = useUser();
    const setUser = useSetUser();

    const onComplete = useCallback(
        (newUser) => {
            setUser(newUser);
            if (onSuccess !== null) {
                onSuccess(newUser);
            }
        },
        [setUser, onSuccess],
    );

    const fieldsNames = useMemo(
        () =>
            fields.reduce((names, { type, name, fields: subFields }) => {
                if (type === 'group') {
                    return [...names, ...subFields.map(({ name: subFieldName }) => subFieldName)];
                }
                return [...names, name];
            }, []),
        [fields],
    );

    const initialFieldsValue = useMemo(
        () =>
            fieldsNames.reduce(
                (map, name) => ({
                    ...map,
                    [name]: user !== null ? user[camelCase(name)] || null : null,
                }),
                {},
            ),
        [user, fieldsNames],
    );

    const {
        fields: formFields,
        onSubmit,
        value,
        errors,
        loading,
        generalError = null,
    } = useForm({
        fields: fieldsNames,
        initialValue: initialFieldsValue,
        action,
        onComplete,
    });

    const renderField = useMemo(() => {
        function renderSubField(field) {
            const { name = null, fields: subFields = null } = field || {};
            return (
                <FieldRow
                    key={`field-${name}`}
                    className={classNames([styles.fieldRow])}
                    {...field}
                    {...formFields[name]}
                    fields={
                        subFields !== null
                            ? subFields.map((subField) => ({
                                  ...subField,
                                  ...formFields[subField.name],
                              }))
                            : null
                    }
                />
            );
        }
        return renderSubField;
    }, [formFields, errors]);

    return (
        <form
            className={classNames([
                styles.container,
                {
                    [styles.isCreate]: isCreate,
                    [styles.loading]: loading,
                },
                themeClassName,
                className,
            ])}
            onSubmit={onSubmit}
            action={action}
        >
            <div className={classNames([styles.note, 'profile-inner'])}>
                {fields.map((field) => renderField(field))}
                {note !== null ? (
                    <div className={classNames([styles.note, 'profile-note'])}>* {note}</div>
                ) : null}
                {footerText !== null ? (
                    <div className={classNames([styles.note, 'profile-note'])}>{footerText}</div>
                ) : null}
                <SubmitButton className={styles.submit}>
                    {submitLabel !== null ? (
                        <p>{submitLabel}</p>
                    ) : (
                        <FormattedMessage defaultMessage="Submit" description="Button label" />
                    )}
                </SubmitButton>
            </div>
            <div className={styles.errors}>
                {generalError !== null ? <div>{generalError}</div> : null}
            </div>
        </form>
    );
}

ProfileForm.propTypes = propTypes;
ProfileForm.defaultProps = defaultProps;

export default ProfileForm;
